import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'

import { KeyingAppEnvironmentConfig } from '../../configuration/keying.app.environment.config'
import { ISymbolData } from '../../models/dev.symbol-data.interface'

export interface CylinderValidationItem {
  keyId: number
  cylinderId: number
}

export interface ValidateCylindersRequest {
  cylinders: CylinderValidationItem[]
}

/**
 * Service for managing key system operations and workflows
 *
 */
@Injectable({
  providedIn: 'root',
})
export class KeySystemService {
  constructor(
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    @Inject(HttpClient) private httpClient: HttpClient
  ) {}

  /**
   * Get count of cylinders with pinnings for a key system
   * @param structureId - Structure ID
   * @param keySystemId - Key System ID
   * @returns Observable with cylinder count
   */
  getCylindersCountWithPinnings(structureId: number, keySystemId: number): Observable<number> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`
    const headers = new HttpHeaders().set(
      'Accept',
      'application/allegion.keying.get.cylinders.count.with.pinnings+json'
    )

    return this.httpClient.get<number>(url, { headers })
  }

  /**
   * Get Duplicate Symbols
   * @param structureId - Structure ID
   * @param keySystemId - Key System ID
   * @returns Observable with Key & Cylinder Symbols
   */
  getDuplicateSymbols(structureId: number, keySystemId: number): Observable<ISymbolData> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`
    const headers = new HttpHeaders().set('Accept', 'application/allegion.keying.get.duplicate.symbol.data+json')

    return this.httpClient.get<ISymbolData>(url, { headers })
  }

  /**
   * Check if a structure has a Secure L Pin (SL) product ordered
   * @param structureId - Structure ID
   * @param keySystemId - Key System ID
   * @returns Observable with boolean indicating if SL product has been ordered
   */
  hasSecureLPinOrder(structureId: number, keySystemId: number): Observable<boolean> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`
    const headers = new HttpHeaders().set('Accept', 'application/allegion.keying.has.secure.lpin.order+json')

    return this.httpClient.get<boolean>(url, { headers })
  }

  validateCylinders(
    structureId: number,
    keySystemId: number,
    payload: ValidateCylindersRequest
  ): Observable<string> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/cylinders/validate`
    return this.httpClient.post<string>(url, payload)
  }
}
